import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useInvites from "../../hooks/useInvites";
import { useUserRole } from "../../context/UserRoleContext";
import { useNotifications } from "../../context/NotificationContext";

const InviteResponseModal = ({
  isOpen,
  onClose,
  inviteId,
  notificationId,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { userData } = useUserRole();
  const { deleteNotification } = useNotifications();
  const { respondToInvite, getReceivedInvites } = useInvites();
  const [invite, setInvite] = useState(null);
  const [relatedInvites, setRelatedInvites] = useState([]);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);
  const staleAutoDeleteDone = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      staleAutoDeleteDone.current = false;
    }
  }, [isOpen]);

  // Self-heal: remove stale actionable rows when invite is already resolved or missing
  useEffect(() => {
    if (!isOpen || !notificationId || loadingInvite) return;
    if (staleAutoDeleteDone.current) return;

    if (error && !invite) {
      staleAutoDeleteDone.current = true;
      deleteNotification(notificationId);
      return;
    }
    if (!invite) return;

    const dateExpired = new Date(invite.expires_at) < new Date();
    const statusExpired = invite.status === "expired";
    const notPending = invite.status !== "pending";

    if (notPending || statusExpired || dateExpired) {
      staleAutoDeleteDone.current = true;
      deleteNotification(notificationId);
    }
  }, [
    isOpen,
    notificationId,
    loadingInvite,
    invite,
    error,
    deleteNotification,
  ]);

  // Fetch invite details and related invites
  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!isOpen || !inviteId) {
        setLoadingInvite(false);
        return;
      }

      try {
        setLoadingInvite(true);
        setError(null);
        setInvite(null);
        setRelatedInvites([]);

        // Fetch all received invites to find related ones
        const response = await getReceivedInvites();

        console.log("Received invites response:", response);
        console.log("Looking for inviteId:", inviteId);

        if (!response) {
          console.error("No response from getReceivedInvites");
          setError("Failed to load invite details - no response");
          setLoadingInvite(false);
          return;
        }

        if (!response.success) {
          console.error("Response not successful:", response.error);
          setError(response.error || "Failed to load invite details");
          setLoadingInvite(false);
          return;
        }

        if (!response.invites || !Array.isArray(response.invites)) {
          console.error(
            "Invalid response format - invites not an array:",
            response,
          );
          setError("Invalid response format");
          setLoadingInvite(false);
          return;
        }

        console.log("Found invites:", response.invites.length);
        console.log(
          "Invite IDs:",
          response.invites.map((inv) => ({
            id: inv.invite_id,
            type: typeof inv.invite_id,
          })),
        );

        // Find the main invite
        // Try multiple formats in case inviteId is string or number
        const inviteIdNum =
          typeof inviteId === "string" ? parseInt(inviteId, 10) : inviteId;
        console.log(
          "Searching for inviteId:",
          inviteId,
          "as number:",
          inviteIdNum,
        );

        const mainInvite = response.invites.find((inv) => {
          const invIdNum =
            typeof inv.invite_id === "string"
              ? parseInt(inv.invite_id, 10)
              : inv.invite_id;
          const matches =
            invIdNum === inviteIdNum ||
            inv.invite_id === inviteId ||
            inv.invite_id === inviteIdNum ||
            String(inv.invite_id) === String(inviteId);
          if (matches) {
            console.log("Found matching invite:", inv);
          }
          return matches;
        });

        if (!mainInvite) {
          console.error(
            "Invite not found. Searched for:",
            inviteId,
            "Available IDs:",
            response.invites.map((inv) => inv.invite_id),
          );
          setError(`Invite not found (ID: ${inviteId})`);
          setLoadingInvite(false);
          return;
        }

        console.log("Found main invite:", mainInvite);

        // Check if expired
        if (mainInvite.status === "expired") {
          setInvite(mainInvite);
          setLoadingInvite(false);
          return;
        }

        if (mainInvite.status !== "pending") {
          setInvite(mainInvite);
          setLoadingInvite(false);
          return;
        }

        setInvite(mainInvite);

        // Find related invites (same sender, recipient, created within 1 minute)
        // This handles batch invites - grouped by sender/recipient/created_at instead of token
        const mainCreatedAt = new Date(mainInvite.created_at);
        const related = response.invites.filter((inv) => {
          if (inv.invite_id === mainInvite.invite_id) return false;
          if (inv.status !== "pending") return false;
          if (inv.sender_id !== mainInvite.sender_id) return false;
          if (inv.recipient_id !== mainInvite.recipient_id) return false;
          const invCreatedAt = new Date(inv.created_at);
          const diffSeconds = Math.abs((invCreatedAt - mainCreatedAt) / 1000);
          return diffSeconds <= 60; // Within 1 minute
        });

        setRelatedInvites(related);
      } catch (err) {
        console.error("Error fetching invite details:", err);
        setError(err.message || "Failed to load invite details");
      } finally {
        setLoadingInvite(false);
      }
    };

    fetchInviteDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inviteId]);

  const handleResponse = async (action) => {
    if (!invite || responding) return;

    try {
      setResponding(true);
      setError(null);

      const result = await respondToInvite(invite.invite_id, action);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result, action);
        }
        onClose();
      }
    } catch (err) {
      setError(err.message || `Failed to ${action} invite`);
    } finally {
      setResponding(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const isExpired = invite && new Date(invite.expires_at) < new Date();
  const daysUntilExpiry =
    invite && !isExpired
      ? Math.ceil(
          (new Date(invite.expires_at) - new Date()) / (1000 * 60 * 60 * 24),
        )
      : null;

  const basePath =
    userData?.role === "artist"
      ? "/artist"
      : userData?.role === "bar_owner"
        ? "/bar-owner"
        : "/bar-admin";

  const inviteNoLongerActionable =
    invite &&
    (invite.status !== "pending" ||
      invite.status === "expired" ||
      isExpired);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="invite-response-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="invite-response-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-response-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="invite-response-modal-header">
          <h2 id="invite-response-modal-title">
            {invite?.invite_type === "artist_member"
              ? "Artist Member Invitation"
              : "Bar Invitation"}
          </h2>
          <button
            type="button"
            className="invite-response-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="invite-response-modal-body">
          {loadingInvite ? (
            <div className="invite-response-loading">
              <p>Loading invitation details...</p>
            </div>
          ) : error && !invite ? (
            <div className="notification-stale-panel">
              <h3 className="notification-stale-title">Invite unavailable</h3>
              <p className="notification-stale-body">
                {error ||
                  "We could not load this invite. It may have been withdrawn, or you already responded."}
              </p>
              <div className="notification-stale-actions">
                <button
                  type="button"
                  className="invite-response-close-btn"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="invite-response-accept-btn"
                  onClick={() => {
                    navigate(`${basePath}/notifications`);
                    onClose();
                  }}
                >
                  View notifications
                </button>
              </div>
            </div>
          ) : inviteNoLongerActionable ? (
            <div className="notification-stale-panel">
              <h3 className="notification-stale-title">
                {isExpired || invite.status === "expired"
                  ? "This invite expired"
                  : "Already handled"}
              </h3>
              <p className="notification-stale-body">
                {isExpired || invite.status === "expired"
                  ? "This invitation is no longer valid. Ask the sender for a new invite if you still need access."
                  : "You already accepted or declined this invite. Nothing else is required here."}
              </p>
              <div className="notification-stale-actions">
                <button
                  type="button"
                  className="invite-response-close-btn"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="invite-response-accept-btn"
                  onClick={() => {
                    navigate(`${basePath}/notifications`);
                    onClose();
                  }}
                >
                  View notifications
                </button>
              </div>
            </div>
          ) : invite ? (
            <>
              {error && (
                <div className="invite-response-error">
                  <p>{error}</p>
                </div>
              )}

              <div className="invite-response-content">
                {invite.sender_name && (
                  <div className="invite-response-sender">
                    <p>
                      <strong>From:</strong> {invite.sender_name}
                    </p>
                  </div>
                )}

                {invite.invite_type === "artist_member" ? (
                  <div className="invite-response-artist">
                    <h3>Invited to join:</h3>
                    <ul>
                      <li>
                        <strong>{invite.artist_name || "Artist Group"}</strong>
                      </li>
                      {relatedInvites.length > 0 &&
                        relatedInvites.map((inv) => (
                          <li key={inv.invite_id}>
                            <strong>{inv.artist_name || "Artist Group"}</strong>
                          </li>
                        ))}
                    </ul>
                    {relatedInvites.length > 0 && (
                      <p className="invite-response-batch-note">
                        You'll be added to all {relatedInvites.length + 1}{" "}
                        artist groups if you accept.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="invite-response-bars">
                    <h3>Invited to:</h3>
                    <ul>
                      <li>
                        <strong>{invite.bar_name}</strong>
                        {invite.bar_address && (
                          <span className="invite-bar-address">
                            {" "}
                            - {invite.bar_address}
                          </span>
                        )}
                      </li>
                      {relatedInvites.length > 0 &&
                        relatedInvites.map((inv) => (
                          <li key={inv.invite_id}>
                            <strong>{inv.bar_name}</strong>
                            {inv.bar_address && (
                              <span className="invite-bar-address">
                                {" "}
                                - {inv.bar_address}
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                    {relatedInvites.length > 0 && (
                      <p className="invite-response-batch-note">
                        You'll be added to all {relatedInvites.length + 1} bars
                        if you accept.
                      </p>
                    )}
                  </div>
                )}

                {invite.message && (
                  <div className="invite-response-message">
                    <h3>Message:</h3>
                    <p>{invite.message}</p>
                  </div>
                )}

                <div className="invite-response-details">
                  <p>
                    <strong>Invited:</strong>{" "}
                    {formatDateTime(invite.created_at)}
                  </p>
                  <p>
                    <strong>Expires:</strong>{" "}
                    <span className={isExpired ? "expired" : ""}>
                      {formatDate(invite.expires_at)}
                      {daysUntilExpiry !== null && (
                        <span className="days-remaining">
                          {" "}
                          ({daysUntilExpiry} day
                          {daysUntilExpiry !== 1 ? "s" : ""} remaining)
                        </span>
                      )}
                      {isExpired && (
                        <span className="expired-badge"> (Expired)</span>
                      )}
                    </span>
                  </p>
                </div>
              </div>

              <div className="invite-response-actions">
                {invite.can_respond === false ? (
                  <p className="invite-response-owner-only">
                    Only the owner can respond to invites.
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      className="invite-response-decline-btn"
                      onClick={() => handleResponse("decline")}
                      disabled={responding || isExpired}
                    >
                      {responding ? "Processing..." : "Decline"}
                    </button>
                    <button
                      type="button"
                      className="invite-response-accept-btn"
                      onClick={() => handleResponse("accept")}
                      disabled={responding || isExpired}
                    >
                      {responding ? "Processing..." : "Accept"}
                    </button>
                  </>
                )}
              </div>

              {isExpired && (
                <div className="invite-response-expired-warning">
                  <p>
                    This invitation has expired and can no longer be accepted.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="invite-response-error">
              <p>Invite not found</p>
              <button
                type="button"
                className="invite-response-close-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteResponseModal;
